import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ImageUpload, PrivateImage } from "@/components/ImageUpload";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Loader2, Landmark, Search, Lock, UserX, UserCheck } from "lucide-react";
import { toast } from "sonner";

const DESIGNATIONS = ["President", "Vice President", "Secretary", "Treasurer", "Trustee"];
const empty = { full_name: "", designation: "Trustee", photo_path: null, appointment_date: "", mobile: "", email: "", active: true };

const errMsg = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg.replace(/^Value error, /, "");
  return "Failed to save trustee";
};

export default function TrusteesAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [showInactive, setShowInactive] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [custom, setCustom] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/trustees").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((t) => (showInactive || t.active) &&
      (!q || `${t.full_name} ${t.designation} ${t.mobile} ${t.email}`.toLowerCase().includes(q)));
  }, [items, query, showInactive]);

  const openAdd = () => { setEditing(null); setForm(empty); setCustom(false); setDialogOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ full_name: t.full_name, designation: t.designation, photo_path: t.photo_path || null, appointment_date: t.appointment_date, mobile: t.mobile || "", email: t.email || "", active: !!t.active });
    setCustom(!DESIGNATIONS.includes(t.designation));
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/trustees/${editing.id}`, form);
      else await api.post("/admin/trustees", form);
      toast.success(editing ? "Trustee updated" : "Trustee added");
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const toggleActive = async (t) => {
    try {
      await api.patch(`/admin/trustees/${t.id}/active`, { active: !t.active });
      toast.success(t.active ? "Trustee deactivated" : "Trustee reactivated");
      load();
    } catch { toast.error("Failed to update status"); }
  };

  return (
    <div data-testid="admin-trustees-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B38728] font-mono">Governance</p>
          <h1 className="font-display text-3xl font-semibold text-[#194324]">Trustees</h1>
        </div>
        <Button onClick={openAdd} className="bg-[#194324] hover:bg-[#2D623A] text-white rounded-full" data-testid="admin-add-trustee-button">
          <Plus className="h-4 w-4 mr-2" /> Add Trustee
        </Button>
      </div>
      <p className="flex items-center gap-2 text-xs text-[#5C7264] mb-5" data-testid="trustees-private-note">
        <Lock className="h-3.5 w-3.5 text-[#B38728]" /> Private — visible only to signed-in administrators. Not shown on the public website.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA99F]" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, designation, mobile, email" className="pl-9 bg-white" data-testid="trustee-search-input" />
        </div>
        <label className="flex items-center gap-2 text-sm text-[#314337]">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="trustee-show-inactive-checkbox" /> Show inactive
        </label>
      </div>

      <div className="bg-white rounded-xl border border-[#E2DAD0] overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#C59B27]" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-[#5C7264]" data-testid="trustees-empty"><Landmark className="h-10 w-10 mx-auto mb-3 text-[#CBD2CC]" />{items.length ? "No trustees match your search." : "No trustees added yet."}</div>
        ) : (
          <>
          <div className="md:hidden divide-y divide-[#E2DAD0]" data-testid="trustees-mobile-list">
            {filtered.map((t) => (
              <div key={t.id} className={`p-4 flex gap-3 ${t.active ? "" : "opacity-60"}`} data-testid={`trustee-card-${t.id}`}>
                {t.photo_path ? <PrivateImage path={t.photo_path} className="h-12 w-12 rounded-full object-cover shrink-0" /> : <div className="h-12 w-12 rounded-full bg-[#F2EEE5] shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-[#194324] [overflow-wrap:anywhere]">{t.full_name}</div>
                      <div className="text-sm text-[#314337]">{t.designation}</div>
                    </div>
                    <Badge className={`border-0 shrink-0 ${t.active ? "bg-[#E8F0EA] text-[#194324]" : "bg-[#F2EEE5] text-[#5C7264]"}`}>{t.active ? "Active" : "Inactive"}</Badge>
                  </div>
                  <div className="text-xs text-[#5C7264] mt-1 space-y-0.5 [overflow-wrap:anywhere]">
                    <div>Appointed {t.appointment_date}</div>
                    {t.mobile && <div>{t.mobile}</div>}
                    {t.email && <div>{t.email}</div>}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`trustee-card-edit-${t.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => toggleActive(t)} data-testid={`trustee-card-toggle-${t.id}`}>{t.active ? "Deactivate" : "Reactivate"}</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F2EEE5]">
                  <TableHead>Photo</TableHead><TableHead>Name</TableHead><TableHead>Designation</TableHead>
                  <TableHead>Appointed</TableHead><TableHead>Mobile</TableHead><TableHead>Email</TableHead>
                  <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((t) => (
                  <TableRow key={t.id} data-testid={`trustee-row-${t.id}`} className={t.active ? "" : "opacity-60"}>
                    <TableCell>{t.photo_path ? <PrivateImage path={t.photo_path} className="h-10 w-10 rounded-full object-cover" /> : <div className="h-10 w-10 rounded-full bg-[#F2EEE5]" />}</TableCell>
                    <TableCell className="font-medium text-[#194324]">{t.full_name}</TableCell>
                    <TableCell>{t.designation}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{t.appointment_date}</TableCell>
                    <TableCell className="text-sm">{t.mobile || "—"}</TableCell>
                    <TableCell className="text-sm">{t.email || "—"}</TableCell>
                    <TableCell><Badge className={`border-0 ${t.active ? "bg-[#E8F0EA] text-[#194324]" : "bg-[#F2EEE5] text-[#5C7264]"}`} data-testid={`trustee-status-${t.id}`}>{t.active ? "Active" : "Inactive"}</Badge></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(t)} data-testid={`trustee-edit-${t.id}`}><Pencil className="h-4 w-4 text-[#2D623A]" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => toggleActive(t)} title={t.active ? "Deactivate" : "Reactivate"} data-testid={`trustee-toggle-active-${t.id}`}>
                        {t.active ? <UserX className="h-4 w-4 text-[#D9531E]" /> : <UserCheck className="h-4 w-4 text-[#2D623A]" />}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          </>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-[#194324]">{editing ? "Edit Trustee" : "Add Trustee"}</DialogTitle><DialogDescription className="sr-only">Trustee details</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2"><Label>Full Name *</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required minLength={2} maxLength={120} data-testid="trustee-form-full-name" /></div>
            <div className="space-y-2">
              <Label>Designation *</Label>
              <select value={custom ? "__custom" : form.designation} onChange={(e) => { const v = e.target.value; if (v === "__custom") { setCustom(true); setForm({ ...form, designation: "" }); } else { setCustom(false); setForm({ ...form, designation: v }); } }} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="trustee-form-designation-select">
                {DESIGNATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                <option value="__custom">Custom…</option>
              </select>
              {custom && <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} placeholder="Enter designation" required minLength={2} maxLength={60} data-testid="trustee-form-custom-designation" />}
            </div>
            <div className="space-y-2"><Label>Appointment Date *</Label><Input type="date" value={form.appointment_date} onChange={(e) => setForm({ ...form, appointment_date: e.target.value })} required data-testid="trustee-form-appointment-date" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Mobile (private)</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="trustee-form-mobile" /></div>
              <div className="space-y-2"><Label>Email (private)</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="trustee-form-email" /></div>
            </div>
            <div className="space-y-2"><Label>Photograph (optional)</Label><ImageUpload value={form.photo_path} onChange={(p) => setForm({ ...form, photo_path: p })} testid="trustee-form-photo" isPrivate /></div>
            <label className="flex items-center gap-2 text-sm text-[#194324]"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="trustee-form-active" /> Active</label>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-[#194324] hover:bg-[#2D623A] text-white" data-testid="trustee-form-submit-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Update" : "Add Trustee"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
